import { useMemo, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import './DataTable.css';

/**
 * TanStack-Table-backed table wrapper (issue #13). Parallel to the plain
 * {@link Table} component; opt-in migration path so an existing page can
 * switch when the operator wants sortable columns without touching every
 * consumer at once.
 *
 * <para>
 * Preact runs @tanstack/react-table via the `react` -> `preact/compat` alias
 * that <c>@preact/preset-vite</c> sets up — the same alias every other
 * "react-flavoured" dependency in the project uses (e.g. gridstack).
 * </para>
 */
export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Id (same shape as returned by <c>rowKey</c>) of the currently
   *  selected row — used for master/detail layouts where the selected row
   *  needs a persistent highlight beyond the transient hover state. */
  selectedRowId?: string | null;
  /** Optional per-row extra class — used to visually flag "eigene IPs"
   *  and similar semantic subtypes. Returned class is appended to the
   *  row's regular class list. */
  rowClassName?: (row: T) => string | undefined;
  emptyLabel?: string;
  initialSorting?: SortingState;
  /** Optional: extra content rendered above the header row (filter inputs, etc.). */
  toolbar?: ComponentChildren;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selectedRowId,
  rowClassName,
  emptyLabel = 'Keine Einträge.',
  initialSorting,
  toolbar,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  const memoColumns = useMemo(() => columns, [columns]);
  const memoRows = useMemo(() => rows, [rows]);

  const table = useReactTable<T>({
    data: memoRows,
    columns: memoColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: rowKey,
  });

  return (
    <div class="hv-data-table__wrap">
      {toolbar ? <div class="hv-data-table__toolbar">{toolbar}</div> : null}
      <table class="hv-data-table">
        <thead>
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id}>
              {group.headers.map((h) => {
                const canSort = h.column.getCanSort();
                const sortDir = h.column.getIsSorted();
                return (
                  <th
                    key={h.id}
                    class={canSort ? 'hv-data-table__th--sortable' : undefined}
                    onClick={canSort ? h.column.getToggleSortingHandler() : undefined}
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                    {canSort ? (
                      <span class="hv-data-table__sort-mark" aria-hidden>
                        {sortDir === 'asc' ? ' ▲' : sortDir === 'desc' ? ' ▼' : ''}
                      </span>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={memoColumns.length} class="hv-data-table__empty">{emptyLabel}</td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => {
              const isSelected = selectedRowId !== undefined && selectedRowId !== null && row.id === selectedRowId;
              const classes = [
                onRowClick ? 'hv-data-table__row--clickable' : null,
                isSelected ? 'hv-data-table__row--selected' : null,
                rowClassName ? rowClassName(row.original) ?? null : null,
              ].filter(Boolean).join(' ') || undefined;
              return (
                <tr
                  key={row.id}
                  class={classes}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
